import { useEffect } from 'react'
import { ParcelleId, ParcelleJson } from 'interfaces/Parcelle'
import { fetchWithCheckAuthorization } from 'utils/fetchWithAuthorization'
import { useAppDispatch, useAppSelector } from 'redux/hooks'
import {
  succeededParcelle,
  loadingParcelle,
  errorParcelle as errorParcelleAction,
  Status,
  showModalBeaucoupDeResultats,
  showParcelleResults,
} from 'redux/features/parcelle/slice'
import {
  makeSelectStatusParcelle,
  makeSelectErrorParcelle,
  makeSelectParcelles,
} from 'redux/features/parcelle/selectors'
import { APIAddressParcelle, APISearchBien } from 'interfaces/API'
import {
  createFavorisFromParcellesResults,
  loading as loadingFavoris,
} from 'redux/features/favoris/slice'
import calculBounds from 'utils/calculBounds'
import geoJsonObjectToCoordinates from 'utils/geoJsonObjectToCoordinates'
import useGoogleMap from './useGoogleMap'

interface ReturnProps {
  statusParcelle: Status
  errorParcelle: string | null
  fetchSearchBien: (d: APISearchBien) => Promise<ParcelleJson[] | null>
  makeMapFitAllResults: () => void
  fetchParcelleAddress: (
    AddressParcelle: APIAddressParcelle
  ) => Promise<ParcelleId[]> | null
}

const useParcelle = (): ReturnProps => {
  const parcelles = useAppSelector(makeSelectParcelles)
  const statusParcelle = useAppSelector(makeSelectStatusParcelle)
  const errorParcelle = useAppSelector(makeSelectErrorParcelle)
  const dispatch = useAppDispatch()
  const map = useGoogleMap()

  const fetchParcelleAddress = async (
    AddressParcelle: APIAddressParcelle
  ): Promise<ParcelleId[]> => {
    const response = await fetchWithCheckAuthorization(
      `/api/address/parcelles`,
      {
        method: 'POST',
        body: JSON.stringify(AddressParcelle),
      }
    )
    const { results }: { results: ParcelleId[] } = await response.json()

    return results
  }

  const populateAdressParcelleIntoParcelleJson = async (
    parcelleJson: ParcelleJson[]
  ) => {
    try {
      let results = parcelleJson
      const parcellesWithoutAddress = parcelleJson.filter(
        ({ parcelleAdresse }: ParcelleJson) => parcelleAdresse === null
      )
      if (parcellesWithoutAddress.length > 0) {
        const response = await fetchParcelleAddress(
          parcellesWithoutAddress.map(
            ({ parcelleUid, parcelleCentre }: ParcelleJson) => ({
              id: parcelleUid,
              lat: parcelleCentre.latitude,
              lng: parcelleCentre.longitude,
            })
          )
        )
        results = parcelleJson.map((parcelle: ParcelleJson) => {
          const { parcelleAdresse, parcelleUid } = parcelle
          if (parcelleAdresse === null) {
            const newAdress = response.find(({ id }) => id === parcelleUid)
            return {
              ...parcelle,
              parcelleAdresse: newAdress ? newAdress.adresse : '',
            }
          }

          return parcelle
        })
      }
      dispatch(succeededParcelle(results))
      if (results.length > 100) dispatch(showModalBeaucoupDeResultats())
      else dispatch(showParcelleResults())
      return results
    } catch (err: unknown) {
      console.error(err)
      dispatch(errorParcelleAction((err as Error).toString()))
      return null
    }
  }

  const makeMapFitAllResults = () => {
    if (parcelles.length === 0) return

    const bounds = calculBounds(
      parcelles
        .map((parcelle) => geoJsonObjectToCoordinates(parcelle.geographie))
        .flat()
    )

    map?.fitBounds(bounds)
    map?.panToBounds(bounds)
  }

  const fetchSearchBien = async (
    dataForm: APISearchBien
  ): Promise<ParcelleJson[] | null> => {
    try {
      dispatch(loadingParcelle())
      const response = await fetchWithCheckAuthorization(`/api/search/bien`, {
        method: 'POST',
        body: JSON.stringify(dataForm),
      })
      const json: { results: ParcelleJson[]; message: string } =
        await response.json()
      if (!response.ok) {
        console.error(json.message)
        if (json.message[0].includes('cityCode'))
          throw Error(
            'Veuillez choisir au moins une localisation pour lancer votre recherche'
          )
        if (json.message[0].includes('surfaceAuSol.max'))
          throw Error(json.message[0])
        throw Error('Une erreur est survenue')
      }
      if (!json.results) return null
      return await populateAdressParcelleIntoParcelleJson(json.results)
    } catch (err: unknown) {
      console.error(err)
      dispatch(errorParcelleAction((err as Error).toString()))
      return null
    }
  }

  useEffect(() => {
    if (statusParcelle === Status.ShowParcelleResults) {
      dispatch(loadingFavoris())
      dispatch(createFavorisFromParcellesResults(parcelles))
    }
  }, [statusParcelle, parcelles, dispatch])

  return {
    statusParcelle,
    errorParcelle,
    fetchSearchBien,
    makeMapFitAllResults,
    fetchParcelleAddress,
  }
}

export default useParcelle
